import { throttle } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

const SCROLL_END_MARGIN = 2;

export const useScrollControls = () => {
	const ref = useRef<HTMLDivElement>(null);
	const [scrollHidden, setScrollHidden] = useState(true);
	const [scrolledAmount, setScrolledAmount] = useState(0);

	const throttledCheckScrollPresentRef = useRef(
		throttle((element: HTMLDivElement) => {
			setScrollHidden(element.scrollWidth === element.offsetWidth);
		}, 100),
	);

	const checkScrollPresent = useCallback(() => {
		if (ref.current === null) {
			return;
		}

		throttledCheckScrollPresentRef.current(ref.current);
	}, []);

	useEffect(() => {
		checkScrollPresent();

		window.addEventListener("resize", checkScrollPresent, { passive: true });

		return () => {
			window.removeEventListener("resize", checkScrollPresent);
		};
	}, [checkScrollPresent]);

	const handleScrollClick = (amount: number) => {
		if (ref.current === null) {
			return;
		}

		ref.current.scrollBy({ left: amount, behavior: "smooth" });
	};

	const isAtScrollStart = () => scrolledAmount === 0;
	const isAtScrollEnd = () => {
		if (ref.current === null) {
			return false;
		}

		return ref.current.scrollWidth - scrolledAmount <= ref.current.offsetWidth + SCROLL_END_MARGIN;
	};

	return {
		ref,
		scrollHidden,
		handleScrollClick,
		handleRefreshScroll: checkScrollPresent,
		isAtScrollStart,
		isAtScrollEnd,
		onScroll: (amount: number) => setScrolledAmount(amount),
	};
};
